import { CoursesListing } from '@/components/CoursesListing'
import { useCleanPath } from '@/hooks/useCleanPath'
import { Course } from '@/kontent/content-types'
import { handleResourceDownload } from '@/legacy-ported/components/support/ResourceBody'
import type { Mapping } from '@/types'
import { isRichtextEmpty } from '@/utils'
import { LEGACY_HEADING_ANCHOR_ATTRIBUTE } from '@/utils/deeplink/hashTarget'
import { getHeadingId } from '@/utils/richtext/getHeadingId'
import type { Elements, ILink } from '@kontent-ai/delivery-sdk'
import clsx from 'clsx'
import parseHTML, {
	DOMNode,
	Element,
	HTMLReactParserOptions,
	Text,
	attributesToProps,
	domToReact,
} from 'html-react-parser'
import { useRouter } from 'next/router'
import React, { ReactNode, forwardRef } from 'react'
import Scrollbar from 'react-smooth-scrollbar'
import { useHeadingIdPrefix } from './contexts/HeadingIdPrefixContext'
import { CommonCopyUrlWrapper } from './ui/copy-to-clipboard/CommonCopyUrlWrapper'

const IMAGE_ID_ATTRIBUTE_IDENTIFIER = 'data-image-id'
const LINKED_ITEM_ID_ATTRIBUTE_IDENTIFIER = 'data-item-id'
const ASSET_ID_ATTRIBUTE_IDENTIFIER = 'data-asset-id'

function isLinkedItem(domNode) {
	return (
		domNode.name === 'object' &&
		domNode.attribs?.type === 'application/kenticocloud'
	)
}

function isImage(domNode) {
	return (
		domNode.name === 'figure' &&
		typeof domNode.attribs?.[IMAGE_ID_ATTRIBUTE_IDENTIFIER] !== 'undefined'
	)
}

function isLink(domNode) {
	return (
		domNode.name === 'a' &&
		typeof domNode.attribs?.[LINKED_ITEM_ID_ATTRIBUTE_IDENTIFIER] !==
			'undefined'
	)
}

function isLinkAsset(domNode: Element) {
	return (
		domNode.name === 'a' &&
		typeof domNode.attribs?.[ASSET_ID_ATTRIBUTE_IDENTIFIER] !== 'undefined'
	)
}

function isHeadingType(tagName) {
	return ['h2', 'h3', 'h4', 'h5'].includes(tagName)
}

// Recursively gather all descendant text so headings whose text is wrapped in
// inline markup (e.g. <strong>, <a>) still yield a non-empty slug for the id.
function getNodeText(node: DOMNode): string {
	if (!node) return ''
	if (node.type === 'text') return (node as Text).data || ''
	const children = (node as Element).children
	if (Array.isArray(children)) {
		return children.map(getNodeText).join('')
	}
	return ''
}

function allowCopyUrlHeadings(tagName) {
	return ['h1', 'h2', 'h3', 'h4', 'h5'].includes(tagName?.toLowerCase())
}

const MathMLTag = ({
	tagName,
	attributes,
	children,
}: {
	tagName: string
	attributes: any
	children: React.ReactNode
}) => {
	const ref = React.useRef<globalThis.Element>(null)

	// Uses useLayoutEffect to manually set the attribute on the DOM node

	// Using a safe hook pattern
	const useIsomorphicLayoutEffect =
		typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect

	useIsomorphicLayoutEffect(() => {
		// This handles open="" (empty string) which is falsy but valid in MathML
		if (ref.current && 'open' in attributes) {
			ref.current.setAttribute('open', attributes.open)
		}
	}, [attributes.open, tagName])

	const { open, ...safeAttributes } = attributes

	return React.createElement(tagName, { ...safeAttributes, ref }, children)
}

export function replaceNode(
	domNode,
	richTextElement: Elements.RichTextElement,
	linkedItems,
	mappings,
	resolveLinkedItem,
	resolveImage,
	resolveLink,
	resolveDomNode,
	headingIds = [],
	suffixForHeading = '',
	disableCopyUrl = false,
	copyUrlPrefix = '',
	prefixForHeading = '',
) {
	const { images, links } = richTextElement
	const attributes = attributesToProps(domNode.attribs)

	if (
		domNode instanceof Element &&
		['mfenced', 'maction', 'menclose'].includes(domNode.name)
	) {
		// Use raw open attribute if present to avoid boolean conversion by attributesToProps
		// Check existence with 'in' operator, not truthiness - open="" is valid in MathML
		const fixedAttributes = { ...attributes }
		if (domNode.attribs && 'open' in domNode.attribs) {
			fixedAttributes.open = domNode.attribs.open
		}

		return (
			<MathMLTag tagName={domNode.name} attributes={fixedAttributes}>
				{domToReact(domNode.children, {
					replace: (node) =>
						replaceNode(
							node,
							richTextElement,
							linkedItems,
							mappings,
							resolveLinkedItem,
							resolveImage,
							resolveLink,
							resolveDomNode,
							headingIds,
							suffixForHeading,
							disableCopyUrl,
							copyUrlPrefix,
							prefixForHeading,
						),
				})}
			</MathMLTag>
		)
	}

	if (resolveLinkedItem && linkedItems) {
		if (isLinkedItem(domNode)) {
			const codeName = domNode.attribs?.['data-codename']
			const linkedItem = linkedItems[codeName]
			return resolveLinkedItem(linkedItem, domNode, domToReact)
		}
	}

	if (resolveImage && images) {
		if (isImage(domNode)) {
			const imageId = domNode.attribs?.[IMAGE_ID_ATTRIBUTE_IDENTIFIER]
			const image = images.find((image) => image.imageId === imageId)
			return resolveImage(image, domNode, domToReact)
		}
	}

	if (isLinkAsset(domNode)) {
		const customAssetBasePath = process.env.NEXT_PUBLIC_ASSETS_BASE_PATH
		let href = domNode.attribs?.href
		const url = new URL(href)
		if (customAssetBasePath && href) {
			// Replace with custom domain
			href = href?.replace(url.origin, customAssetBasePath)
		}

		return (
			<a
				onClick={handleResourceDownload}
				{...attributes}
				href={href}
				target="_blank"
				rel="noopener noreferrer noindex nofollow"
				download
			>
				{domToReact(domNode.children)}
			</a>
		)
	}

	if (resolveLink && links) {
		if (isLink(domNode)) {
			const linkId =
				domNode.attribs?.[LINKED_ITEM_ID_ATTRIBUTE_IDENTIFIER]
			const link = links?.find((link) => link.linkId === linkId)
			return resolveLink(link, mappings, domNode, domToReact)
		}
	}

	if (domNode instanceof Element && isHeadingType(domNode.name)) {
		const text = domNode.children.map(getNodeText).join('')
		const baseId = getHeadingId(text, {
			prefix: prefixForHeading,
			suffix: suffixForHeading,
		})
		headingIds.push(baseId)
		const duplicateCount =
			headingIds.filter((heading) => heading === baseId).length - 1
		const withDuplicateCount = (value: string) =>
			duplicateCount ? value + '-' + duplicateCount.toString() : value
		const id = withDuplicateCount(baseId)

		// The id this heading had before prefixes existed, so links shared back then still resolve
		// (see `findHashTargetElement`)
		const headingAttributes = prefixForHeading
			? {
					...attributes,
					[LEGACY_HEADING_ANCHOR_ATTRIBUTE]: withDuplicateCount(
						getHeadingId(text, { suffix: suffixForHeading }),
					),
			  }
			: attributes

		return (
			<HeadingComponent
				as={domNode.name}
				id={id}
				attributes={headingAttributes}
				disableCopyUrl={
					disableCopyUrl || !allowCopyUrlHeadings(domNode.name)
				}
				copyUrlPrefix={copyUrlPrefix}
			>
				{domToReact(domNode.children)}
			</HeadingComponent>
		)
	}
	if (domNode instanceof Element && domNode.name === 'table') {
		// Find tbody element - skip text nodes (whitespace) that may appear before tbody
		const tBody = domNode.children?.find(
			(child: Element) => child.type === 'tag' && child.name === 'tbody',
		) as Element

		// Safety check: if table has no tbody or tbody has no children, render table as-is
		if (!tBody?.children) {
			return <table {...attributes}>{domToReact(domNode.children)}</table>
		}

		const tRows = tBody.children.filter(
			(item: Element) => item.type === 'tag' && item.name === 'tr',
		) as Element[]

		// Safety check: if no rows found, render table as-is
		if (!tRows.length) {
			return <table {...attributes}>{domToReact(domNode.children)}</table>
		}

		return (
			<Scrollbar
				className={clsx(
					'nsw-table [&_.scrollbar-thumb]:bg-nsw-brand-dark [&_.scrollbar-thumb]:w-1.5 [&_.scrollbar-thumb]:h-1.5',
					'[&_.scrollbar-track-x]:h-1.5 [&_.scrollbar-track-y]:w-1.5',
				)}
				damping={0.33}
				alwaysShowTracks
				continuousScrolling={false}
				css={{
					'&:has([id*=popper-])': {
						overflow: 'visible !important',
					},
				}}
			>
				<table {...attributes}>
					<thead>
						<tr>
							{(tRows[0]?.children || [])
								.filter(
									(tRowChild: Element) =>
										tRowChild?.type === 'tag' &&
										tRowChild?.name === 'td',
								)
								.map((tRowChild: Element, index) => {
									const cellAttributes = attributesToProps(
										tRowChild.attribs,
									)
									return (
										<th key={index} {...cellAttributes}>
											{domToReact(tRowChild.children, {
												replace: (node) =>
													replaceNode(
														node,
														richTextElement,
														linkedItems,
														mappings,
														resolveLinkedItem,
														resolveImage,
														resolveLink,
														resolveDomNode,
														headingIds,
														suffixForHeading,
														disableCopyUrl,
														copyUrlPrefix,
														prefixForHeading,
													),
											})}
										</th>
									)
								})}
						</tr>
					</thead>
					<tbody>
						{tRows.slice(1).map((row, rowIndex) => (
							<tr key={rowIndex}>
								{((row?.children as Element[]) || [])
									.filter(
										(cell) =>
											cell?.type === 'tag' &&
											cell?.name === 'td',
									)
									.map((cell, cellIndex) => {
										const cellAttributes =
											attributesToProps(cell.attribs)
										return (
											<td
												key={cellIndex}
												{...cellAttributes}
											>
												{domToReact(cell.children, {
													replace: (node) =>
														replaceNode(
															node,
															richTextElement,
															linkedItems,
															mappings,
															resolveLinkedItem,
															resolveImage,
															resolveLink,
															resolveDomNode,
															headingIds,
															suffixForHeading,
															disableCopyUrl,
															copyUrlPrefix,
															prefixForHeading,
														),
												})}
											</td>
										)
									})}
							</tr>
						))}
					</tbody>
				</table>
			</Scrollbar>
		)
	}

	if (resolveDomNode) {
		return resolveDomNode(domNode, domToReact)
	}
}

export interface RichTextComponentProps {
	richTextElement: Elements.RichTextElement
	linkedItems?: any
	mappings?: Mapping[]
	resolveLinkedItem?: any
	resolveImage?: any
	resolveLink?: (
		_link: ILink,
		_mappings: Mapping[],
		_domNode: Element,
		_domToReact: (
			_nodes: DOMNode[],
			_options?: HTMLReactParserOptions,
		) => string | JSX.Element | JSX.Element[],
	) => ReactNode
	resolveDomNode?: any
	className?: string
	suffixForHeading?: string
	renderFnBefore?: (_e: string | JSX.Element | JSX.Element[]) => ReactNode
	disableCopyUrl?: boolean
	copyUrlPrefix?: string
	resolveCourseItems?: Course[]
}

export function getDomNode(
	{
		richTextElement,
		linkedItems,
		mappings,
		resolveLinkedItem,
		resolveImage,
		resolveLink,
		resolveDomNode,
		suffixForHeading,
		disableCopyUrl,
		copyUrlPrefix,
	}: RichTextComponentProps,
	prefixForHeading = '',
): string | JSX.Element | JSX.Element[] {
	const cleanedValue = richTextElement?.value?.replace(/(\n|\r)+/, '')
	const headingsStorage = []
	return parseHTML(cleanedValue, {
		replace: (domNode) => {
			return replaceNode(
				domNode,
				richTextElement,
				linkedItems,
				mappings,
				resolveLinkedItem,
				resolveImage,
				resolveLink,
				resolveDomNode,
				headingsStorage,
				suffixForHeading,
				disableCopyUrl,
				copyUrlPrefix,
				prefixForHeading,
			)
		},
	})
}

function RichTextComponent(props: RichTextComponentProps) {
	const {
		className,
		richTextElement,
		linkedItems,
		mappings,
		resolveDomNode,
		resolveImage,
		resolveLink,
		resolveLinkedItem,
		suffixForHeading,
		renderFnBefore,
		disableCopyUrl,
		copyUrlPrefix,
		resolveCourseItems,
		...rest
	} = props
	const headingIdPrefix = useHeadingIdPrefix()
	const result = getDomNode(props, headingIdPrefix)
	return (
		<>
			{renderFnBefore && renderFnBefore(result)}
			<div {...rest} className={className}>
				{!isRichtextEmpty(richTextElement.value) && result}
			</div>
			{resolveCourseItems && (
				<CoursesListing
					courseItems={resolveCourseItems}
					linkedItems={linkedItems}
					mappings={mappings}
				/>
			)}
		</>
	)
}

export interface HeadingProps {
	as?: string
	id: string
	attributes: any
	children: React.ReactNode
	disableCopyUrl?: boolean
	copyUrlPrefix?: string
}

export const HeadingComponent = forwardRef(
	(
		{
			as: Tag = 'h2',
			id,
			attributes,
			children,
			disableCopyUrl = false,
			copyUrlPrefix = '',
		}: HeadingProps,
		ref,
	) => {
		const { asPath } = useRouter()
		const path = useCleanPath(asPath, (_path) => {
			const p = _path
			p.hash = id
			return p
		})

		if (disableCopyUrl) {
			return (
				<Tag ref={ref} id={id} {...attributes}>
					{children}
				</Tag>
			)
		}

		const urlPrefix = copyUrlPrefix
			? copyUrlPrefix
			: path.pathname + path.search

		return (
			<CommonCopyUrlWrapper url={urlPrefix + path.hash}>
				<Tag ref={ref} id={id} {...attributes}>
					{children}
				</Tag>
			</CommonCopyUrlWrapper>
		)
	},
)

export default RichTextComponent
